import { useState } from 'react';
import { format, differenceInDays } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  MoreHorizontal, 
  Download, 
  Trash2, 
  Building2, 
  Search,
  RefreshCw,
} from 'lucide-react';
import { ContractDocument, useContractDocuments } from '@/hooks/useContractDocuments';
import { useNavigate } from 'react-router-dom';

interface ContractsListProps {
  contracts: ContractDocument[];
}

export function ContractsList({ contracts }: ContractsListProps) {
  const navigate = useNavigate();
  const { downloadContract, deleteContract } = useContractDocuments();
  const [searchQuery, setSearchQuery] = useState('');
  const [companyTypeFilter, setCompanyTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const getExpiryStatus = (expiryDate?: string): { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' } => {
    if (!expiryDate) return { label: 'No Expiry', variant: 'secondary' };
    
    const daysUntilExpiry = differenceInDays(new Date(expiryDate), new Date());
    
    if (daysUntilExpiry < 0) return { label: 'Expired', variant: 'destructive' };
    if (daysUntilExpiry <= 30) return { label: `${daysUntilExpiry}d left`, variant: 'destructive' };
    if (daysUntilExpiry <= 90) return { label: `${daysUntilExpiry}d left`, variant: 'outline' };
    return { label: 'Active', variant: 'default' };
  };

  const filteredContracts = contracts.filter((contract) => {
    // Search filter
    const matchesSearch = 
      contract.document_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contract.company?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contract.contract_parties?.toLowerCase().includes(searchQuery.toLowerCase());

    // Company type filter
    const matchesCompanyType = 
      companyTypeFilter === 'all' || 
      contract.company?.company_type === companyTypeFilter;

    // Status filter
    let matchesStatus = true;
    if (statusFilter === 'expiring_soon') {
      const expiryDate = contract.expiry_date;
      if (expiryDate) {
        const daysUntilExpiry = differenceInDays(new Date(expiryDate), new Date());
        matchesStatus = daysUntilExpiry > 0 && daysUntilExpiry <= 30;
      } else {
        matchesStatus = false;
      }
    } else if (statusFilter === 'expired') {
      matchesStatus = contract.expiry_date ? new Date(contract.expiry_date) < new Date() : false;
    } else if (statusFilter === 'active') {
      matchesStatus = !contract.expiry_date || new Date(contract.expiry_date) > new Date();
    }

    return matchesSearch && matchesCompanyType && matchesStatus;
  });

  const handleDelete = async () => {
    if (deleteId) {
      await deleteContract(deleteId);
      setDeleteId(null);
    }
  };

  const formatCurrency = (value?: number) => {
    if (!value) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search contracts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={companyTypeFilter} onValueChange={setCompanyTypeFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Company Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="contractor">Contractors</SelectItem>
            <SelectItem value="client">Clients</SelectItem>
            <SelectItem value="vendor">Vendors</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Contract Type</TableHead>
              <TableHead>Parties</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Expiration</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredContracts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No contracts found
                </TableCell>
              </TableRow>
            ) : (
              filteredContracts.map((contract) => {
                const expiryStatus = getExpiryStatus(contract.expiry_date);
                return (
                  <TableRow key={contract.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{contract.company?.name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground capitalize">
                            {contract.company?.company_type}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{contract.document_type}</p>
                        {contract.auto_renewal && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <RefreshCw className="h-3 w-3" />
                            Auto-renewal
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {contract.contract_parties || '—'}
                    </TableCell>
                    <TableCell>{formatCurrency(contract.contract_value)}</TableCell>
                    <TableCell>
                      {contract.expiry_date 
                        ? format(new Date(contract.expiry_date), 'MMM d, yyyy')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={expiryStatus.variant}>
                        {expiryStatus.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => downloadContract(contract.id)}>
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => navigate(`/companies?id=${contract.company_id}`)}
                          >
                            <Building2 className="h-4 w-4 mr-2" />
                            View Company
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-destructive"
                            onClick={() => setDeleteId(contract.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contract</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this contract? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
